import React, { useEffect, useState } from "react";
import {
  Typography,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useContent } from "../context/ContentContext";
import API from "../services/API";
import { AppButton, AppTextField, Panel } from "../components/ui";
import "./SystemsPage.scss";

const SystemsPage = () => {
  const { mySystems } = useContent();
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editedSystems, setEditedSystems] = useState({});

  // States for sorting
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // States for delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [systemToDelete, setSystemToDelete] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update local state when context systems change.
  useEffect(() => {
    if (mySystems) {
      setSystems(mySystems);
      setLoading(false);
    }
  }, [mySystems]);

  // ----- Edit Existing System Handlers -----
  const handleEdit = async (systemId) => {
    try {
      const response = await API.get(`systems/${systemId}`, {
        requiresAuth: true,
      });
      const fullSystem = response.data;
      setEditingId(systemId);
      setEditedSystems((prev) => ({
        ...prev,
        [systemId]: {
          name: fullSystem.name,
          description: fullSystem.description,
          fullSystem: fullSystem,
        },
      }));
    } catch (error) {
      console.error("Error fetching full system data:", error);
    }
  };

  const handleChange = (systemId, field, value) => {
    setEditedSystems((prev) => ({
      ...prev,
      [systemId]: {
        ...prev[systemId],
        [field]: value,
      },
    }));
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const handleSave = async (systemId) => {
    const currentData = editedSystems[systemId];
    if (!currentData || !currentData.fullSystem) return;
    const updatedSystem = {
      ...currentData.fullSystem,
      name: currentData.name,
      description: currentData.description,
    };

    try {
      await API.post("systems/", updatedSystem, { requiresAuth: true });
      setSystems((prev) =>
        prev.map((sys) =>
          sys.id === systemId
            ? {
                ...sys,
                name: updatedSystem.name,
                description: updatedSystem.description,
              }
            : sys
        )
      );
      setEditingId(null);
    } catch (error) {
      console.error("Error saving system:", error);
    }
  };

  // ----- Delete System Handlers -----
  const handleDeleteClick = (system) => {
    setSystemToDelete(system);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!systemToDelete) return;
    try {
      await API.get(`systems/${systemToDelete.id}/delete`, {
        requiresAuth: true,
      });
      setSystems((prev) => prev.filter((sys) => sys.id !== systemToDelete.id));
    } catch (error) {
      console.error("Error deleting system:", error);
    } finally {
      setDeleteConfirmOpen(false);
      setSystemToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setSystemToDelete(null);
  };

  // ----- Sorting -----
  const sortedSystems = [...systems].sort((a, b) => {
    const aValue = a[sortField] || "";
    const bValue = b[sortField] || "";
    if (sortField === "name") {
      return sortOrder === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    } else if (sortField === "dateModified") {
      return sortOrder === "asc"
        ? new Date(aValue) - new Date(bValue)
        : new Date(bValue) - new Date(aValue);
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="systemsPage">
        <div className="systemsLoading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="systemsPage">
      <Typography className="systemsTitle" variant="h4">
        Systems
      </Typography>

      {/* Sorting Controls */}
      <div className="systemsSortRow">
        <div className="systemsSortGroup">
          <ButtonGroup size="small" aria-label="sort systems">
            <AppButton
              kind={sortField === "name" ? "primary" : "secondary"}
              onClick={() => setSortField("name")}
            >
              Sort: Name
            </AppButton>
            <AppButton
              kind={sortField === "dateModified" ? "primary" : "secondary"}
              onClick={() => setSortField("dateModified")}
            >
              Sort: Date Updated
            </AppButton>
          </ButtonGroup>
        </div>

        <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
          <IconButton
            className="systemsSortArrow"
            size="small"
            onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Systems List */}
      <div className="systemsList app-row-list">
        {sortedSystems.map((system) => (
          <Panel key={system.id} className="systemCard" hoverable>
            {editingId === system.id ? (
              <div className="systemEditForm">
                <div className="systemEditFields">
                  <AppTextField
                    label="Display Name"
                    value={editedSystems[system.id].name}
                    onChange={(e) => handleChange(system.id, "name", e.target.value)}
                    fullWidth
                  />
                  <AppTextField
                    label="Description"
                    value={editedSystems[system.id].description}
                    onChange={(e) => handleChange(system.id, "description", e.target.value)}
                    fullWidth
                  />
                </div>

                <div className="systemEditActions">
                  <AppButton onClick={() => handleSave(system.id)}>
                    Save
                  </AppButton>
                  <AppButton kind="ghost" onClick={handleCancel}>
                    Cancel
                  </AppButton>
                </div>
              </div>
            ) : (
              <div className="systemCardContent">
                <div className="systemCardInfo">
                  <div className="systemCardName">{system.name || "No Name"}</div>
                  {system.status?.type && (
                    <div className="systemCardStatus">{system.status?.type}</div>
                  )}
                </div>

                <div className="systemCardDesc">
                  {system.description || "No Description"}
                </div>

                <div className="systemCardActions">
                  <AppButton
                    className="systemCardBtn"
                    kind="secondary"
                    onClick={() => handleEdit(system.id)}
                  >
                    Edit
                  </AppButton>
                  <AppButton
                    className="systemCardBtn"
                    kind="danger"
                    onClick={() => handleDeleteClick(system)}
                  >
                    Delete
                  </AppButton>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {systemToDelete ? systemToDelete.name || "this system" : "this system"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={cancelDelete}>
            Cancel
          </AppButton>
          <AppButton kind="danger" onClick={confirmDelete}>
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SystemsPage;
